import { startAppLoadSpan } from './sentry-init';
import './datadog-init';
import { FronteggProvider } from '@frontegg/react';
import { GlobalStyles, ThemeProvider } from '@mui/material';
import { QueryClientProvider } from '@tanstack/react-query';
import { AgChartsCommunityModule } from 'ag-charts-community';
import { ModuleRegistry } from 'ag-grid-community';
import { AllEnterpriseModule, LicenseManager } from 'ag-grid-enterprise';
import { Provider } from 'jotai';
import { withLDProvider } from 'launchdarkly-react-client-sdk';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { createBrowserRouter, createRoutesFromElements, Route, RouterProvider } from 'react-router';
import { AppLoader } from './AppLoader';
import { RoutedApp } from './AppRoutes2';
import './assets/fonts/fonts.css';
import { AuthWrapperFrontEgg } from './AuthWrapperFrontEgg';
import { DebugAuthProvider } from './debug/DebugAuthProvider';
import { isDebugMode } from './debug/debugUser';
import { BrowserGuard } from './components/BrowserGuard/BrowserGuard';
import { registerDynamicImportReloadListener } from './components/Fallback/GenericErrorFallback';
import { RouteErrorFallback } from './components/Fallback/RouteErrorFallback';
import { LoadingBarProvider } from './components/LoadingBar/LoadingBarContext';
import { AgGridLicense, FrontEggContextOptions } from './constants/Licenses';
import { HydrateAtoms, queryClient } from './services/MaggieQueryClient';
import { globalStyles } from './theme/GlobalStyles';
import { greycroftTheme } from './theme/material-custom';
import { getEnvVariable, isLocal } from './util/environment';
import { FeatureFlags, getFeatureFlagsDefaults } from './util/feature-flags';
import { getForesightStore } from './util/jotai-store';
import { initYup } from './util/yup-bootstrap';

ModuleRegistry.registerModules([AllEnterpriseModule.with(AgChartsCommunityModule)]);
LicenseManager.setLicenseKey(AgGridLicense);
initYup();
startAppLoadSpan();
registerDynamicImportReloadListener();

const debugMode = isDebugMode();

const appContent = (
  <LoadingBarProvider>
    <ThemeProvider theme={greycroftTheme}>
      <GlobalStyles styles={globalStyles} />
      <BrowserGuard>
        {debugMode ? (
          <DebugAuthProvider>
            <AppLoader>
              <RoutedApp />
            </AppLoader>
          </DebugAuthProvider>
        ) : (
          <AuthWrapperFrontEgg>
            <AppLoader>
              <RoutedApp />
            </AppLoader>
          </AuthWrapperFrontEgg>
        )}
      </BrowserGuard>
    </ThemeProvider>
  </LoadingBarProvider>
);

const router = createBrowserRouter(
  createRoutesFromElements(
    <Route
      path='/*'
      errorElement={<RouteErrorFallback />}
      element={
        debugMode ? (
          appContent
        ) : (
          <FronteggProvider
            contextOptions={FrontEggContextOptions}
            hostedLoginBox={true}
            authOptions={{
              hostedLoginOptions: {
                loadUserOnFirstLoad: true,
              },
            }}
            entitlementsOptions={{ enabled: true }}
          >
            {appContent}
          </FronteggProvider>
        )
      }
    ></Route>
  )
);

const root = createRoot(document.getElementById('root')!);

function Index() {
  return (
    <StrictMode>
      {/* see https://jotai.org/docs/extensions/query#referencing-the-same-instance-of-query-client-in-your-project */}
      <QueryClientProvider client={queryClient}>
        <Provider store={getForesightStore()}>
          <HydrateAtoms>
            <RouterProvider router={router} />
          </HydrateAtoms>
        </Provider>
      </QueryClientProvider>
    </StrictMode>
  );
}

// Local-dev flag overrides. Only applied when running the Vite dev server
// (isLocal()), never in built/deployed envs. Lets Document Hub and the Field
// Manager be exercised locally without configuring the flags in LaunchDarkly.
// Remove each once it is managed in the LD dashboard.
function getBootstrapFlags() {
  const flags = getFeatureFlagsDefaults();
  if (isLocal()) {
    flags[FeatureFlags.showDocumentHub] = true;
    flags[FeatureFlags.showFieldsManager] = true;
  }
  return flags;
}

const IndexWithLd = withLDProvider({
  clientSideID: getEnvVariable('VITE_LAUNCH_DARKLY_CLIENT_ID'),
  flags: getBootstrapFlags(),
  reactOptions: {
    useCamelCaseFlagKeys: false,
  },
})(Index);

root.render(<IndexWithLd />);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
// reportWebVitals(console.log);
